// 当日の出展者受付 (受付端末 / 未受付の一覧) の判定ロジック。
//
// 受付端末 (admin-dashboard-2026 の /kiosk) は回線が落ちても動くように、出展者一覧と
// 受付記録を手元に持って自分で判定する。API 側も同じ判定で数えるので、ここに集約する。
//
// 用語:
// - 1 日目 = 土曜 (saturday)、2 日目 = 日曜 (sunday)
// - 受付記録 (CheckinRecord) は追記型のログ。取り消しても行は消さず cancelledAt を入れる
// - 日曜は、土曜に受付済みの両日出展者は受付不要 (開催概要の運用)。
//   日曜の「本日の受付 ○ / ○」は、日曜に受付が必要な人 (日曜のみ＋土曜に受付していない
//   両日出展者、日曜欠席は除く) だけを数える。分母−分子＝未受付の一覧の件数。

import { hallOfBooth2026 } from './venue2026';

export type EventDay = 1 | 2;
import type { ExhibitDay } from '../exhibitor/core';

export const EXHIBIT_DAY_OF_EVENT_DAY: Record<EventDay, ExhibitDay> = {
  1: 'saturday',
  2: 'sunday',
};

// 受付端末が持つ出展者の情報。氏名・メール・電話など非公開の情報は含めない
// (受付端末の画面は後ろに並んでいる人にも見えるため)。
export interface KioskExhibitor {
  id: string;
  applicationNumber: number;
  exhibitorName: string;
  // exhibitors.booth_number はカンマ区切りで複数持てる (1 組で 2 ブース使う場合)。
  boothNumbers: string[];
  exhibitDays: ExhibitDay[];
  tableCount: number;
  chairCount: number;
  // 申込フォームの「電源」(100VA 単位)。列名は power_watts だが単位は VA として表示する。
  powerWatts: number;
  // exhibitors.status。受付できるのは paid だけ。
  status: string;
}

// checkin: 受付した / already: 同じ日にもう一度入力した (「受付済みです」。受付には数えない)
// not_required: 日曜に土曜受付済みの人が番号を入れた (時刻の分析用)
// blocked: 受付端末で止めた (要対応) / absent_seen: 巡回でブースにいないのを確認した
export type CheckinKind = 'checkin' | 'already' | 'not_required' | 'blocked' | 'absent_seen';
// self: 出展者本人が受付端末で入力 / list: スタッフが未受付の一覧から操作
export type CheckinSource = 'self' | 'list';
export type BlockedReason = 'unpaid' | 'cancelled' | 'not_confirmed' | 'wrong_day';

export interface CheckinRecord {
  id: string;
  exhibitorId: string;
  eventDay: EventDay;
  kind: CheckinKind;
  source: CheckinSource;
  reason?: BlockedReason | null;
  // 操作した時刻 (端末の時計, ISO)。回線断で後から送った場合も押した時刻になる。
  recordedAt: string;
  cancelledAt?: string | null;
  deviceId?: string | null;
  // 記録したアカウント (bauth_user.id)。記録の出どころを残すためのもの。
  // 受付の表はイベントで 1 つなので、表示や取消をこれで絞ってはいけない
  // (受付端末のアカウントも admin も、どの端末・アカウントで付けた受付でも取消できる)
  recordedBy?: string | null;
}

// admin が事前の連絡をもとに付ける、日ごとの状態。
export type DayStatusKind = 'absent' | 'late';

export interface DayStatus {
  exhibitorId: string;
  eventDay: EventDay;
  status: DayStatusKind;
  // 遅刻連絡の到着予定 (HH:MM)。不明なら null。
  expectedTime?: string | null;
}

export const BLOCKED_REASON_LABEL: Record<BlockedReason, string> = {
  unpaid: '支払い未確認',
  cancelled: 'キャンセル済み',
  not_confirmed: '出展未確定',
  wrong_day: '出展日が異なる',
};

// ─── 表示 ──────────────────────────────────────────────

export function parseBoothNumbers(raw: string | null | undefined): string[] {
  if (!raw) return [];
  return raw
    .split(',')
    .map((b) => b.trim())
    .filter((b) => b.length > 0);
}

export function hallOfBooths2026(booths: string[]): string | undefined {
  for (const b of booths) {
    const hall = hallOfBooth2026(b);
    if (hall) return hall;
  }
  return undefined;
}

export function formatExhibitDays(days: ExhibitDay[]): string {
  const sat = days.includes('saturday');
  const sun = days.includes('sunday');
  if (sat && sun) return '両日';
  if (sat) return '土のみ';
  if (sun) return '日のみ';
  return '—';
}

export function formatTables(count: number): string {
  return count > 0 ? `机 ${count}本` : '机なし';
}

export function formatChairs(count: number): string {
  return count > 0 ? `椅子 ${count}脚` : '椅子なし';
}

// 開催概要・申込フォームと同じ「電源あり(200VA)」「電源なし」。
export function formatPower(va: number): string {
  return va > 0 ? `電源あり(${va}VA)` : '電源なし';
}

// 受付端末の画面は後ろに並ぶ人にも見えるので、メールアドレスは伏せて出す。
// takahito+kiosk@example.com → ta***@example.com
export function maskEmail(email: string): string {
  const at = email.indexOf('@');
  if (at <= 0) return '***';
  return `${email.slice(0, Math.min(2, at))}***${email.slice(at)}`;
}

// ─── 並び順 ────────────────────────────────────────────

// 配置番号の自然順 (A-2 < A-10)。配置番号の無いものは後ろ。
export function compareBooth(a: string | undefined, b: string | undefined): number {
  if (!a && !b) return 0;
  if (!a) return 1;
  if (!b) return -1;
  const [la, na] = a.split('-');
  const [lb, nb] = b.split('-');
  if (la !== lb) return (la ?? '').localeCompare(lb ?? '');
  return (parseInt(na ?? '', 10) || 0) - (parseInt(nb ?? '', 10) || 0);
}

export function compareExhibitorsByBooth(a: KioskExhibitor, b: KioskExhibitor): number {
  const byBooth = compareBooth(a.boothNumbers[0], b.boothNumbers[0]);
  if (byBooth !== 0) return byBooth;
  return a.applicationNumber - b.applicationNumber;
}

// ─── 日付 ──────────────────────────────────────────────

// 日本時間の日付 (YYYY-MM-DD)。
export function toJstDateString(date: Date): string {
  const jst = new Date(date.getTime() + 9 * 60 * 60 * 1000);
  return jst.toISOString().slice(0, 10);
}

// 開催日 (event_settings の event_start_date / event_end_date の日付部分) から、
// その日が何日目かを返す。会期外なら null。
export function eventDayOf(
  jstDate: string,
  eventDates: { day1: string; day2: string | null }
): EventDay | null {
  if (jstDate === eventDates.day1) return 1;
  if (eventDates.day2 && jstDate === eventDates.day2) return 2;
  return null;
}

// ─── 番号の照合 ────────────────────────────────────────

export type KioskInputKind = 'booth' | 'number';

// 入力文字列は「C-12」(配置番号) か「42」(出展者番号)。英字が先頭にだけ入る。
export function kioskInputKind(input: string): KioskInputKind | null {
  if (input.length === 0) return null;
  return /^[A-Z]/.test(input) ? 'booth' : 'number';
}

export type KioskLookup =
  | { type: 'empty' }
  // 打ち足せば該当する番号がある (例: C-1 の時点で C-12 がある)
  | { type: 'partial' }
  // どう打ち足しても該当が無い
  | { type: 'none' }
  | { type: 'match'; exhibitor: KioskExhibitor };

export function lookupKioskInput(
  input: string,
  exhibitors: KioskExhibitor[],
  day: EventDay
): KioskLookup {
  const kind = kioskInputKind(input);
  if (!kind) return { type: 'empty' };

  if (kind === 'number') {
    // 「042」のように先頭に 0 が付いたものは 42 番とみなさない (サーバも入力の文字列で照合するので、
    // 画面で一致させるとサーバに弾かれる)。出展者番号に先頭の 0 は無いので、打ち間違いとして扱う
    const exact = exhibitors.find((e) => String(e.applicationNumber) === input);
    if (exact) return { type: 'match', exhibitor: exact };
    const hasLonger = exhibitors.some((e) => {
      const s = String(e.applicationNumber);
      return s.length > input.length && s.startsWith(input);
    });
    return hasLonger ? { type: 'partial' } : { type: 'none' };
  }

  // 配置番号。同じブースを土曜と日曜で別の出展者が使うことがある (配置表の「D-7土」「D-7日」)
  // ので、その日に出展する人を優先する。その日の出展者がいなければ、別の日の出展者を返す
  // (本人が出展日を間違えて来た場合に「出展日が異なります」を出すため)。
  const exact = exhibitors.filter((e) => e.boothNumbers.includes(input));
  if (exact.length > 0) {
    const today = EXHIBIT_DAY_OF_EVENT_DAY[day];
    const sorted = [...exact].sort((a, b) => {
      const ta = a.exhibitDays.includes(today) ? 0 : 1;
      const tb = b.exhibitDays.includes(today) ? 0 : 1;
      if (ta !== tb) return ta - tb;
      const pa = a.status === 'paid' ? 0 : 1;
      const pb = b.status === 'paid' ? 0 : 1;
      return pa - pb;
    });
    return { type: 'match', exhibitor: sorted[0]! };
  }
  const hasLonger = exhibitors.some((e) =>
    e.boothNumbers.some((b) => b.length > input.length && b.startsWith(input))
  );
  return hasLonger ? { type: 'partial' } : { type: 'none' };
}

// ─── 日ごとの状態 ──────────────────────────────────────

export interface ExhibitorDayState {
  exhibitor: KioskExhibitor;
  exhibitsToday: boolean;
  paid: boolean;
  // その日の受付 (取り消していないもの) の最初の時刻
  checkedInAt: string | null;
  // 日曜: 土曜に受付済みの両日出展者 (本日の受付は不要)
  notRequired: boolean;
  saturdayCheckedInAt: string | null;
  // 欠席連絡。ただし本人が来て受付したら欠席扱いにしない
  absent: boolean;
  late: { expectedTime: string | null } | null;
  // 巡回で不在を確認した最後の時刻
  lastAbsentSeenAt: string | null;
  // 「本日の受付 ○ / ○」の分母に入るか
  needsCheckin: boolean;
}

function earliest(records: CheckinRecord[]): string | null {
  let min: string | null = null;
  for (const r of records) {
    if (min === null || r.recordedAt < min) min = r.recordedAt;
  }
  return min;
}

function latest(records: CheckinRecord[]): string | null {
  let max: string | null = null;
  for (const r of records) {
    if (max === null || r.recordedAt > max) max = r.recordedAt;
  }
  return max;
}

export function computeDayStates(
  exhibitors: KioskExhibitor[],
  records: CheckinRecord[],
  dayStatuses: DayStatus[],
  day: EventDay
): ExhibitorDayState[] {
  const active = records.filter((r) => !r.cancelledAt);
  const byExhibitor = new Map<string, CheckinRecord[]>();
  for (const r of active) {
    const list = byExhibitor.get(r.exhibitorId);
    if (list) list.push(r);
    else byExhibitor.set(r.exhibitorId, [r]);
  }
  const statusOf = new Map<string, DayStatus>();
  for (const s of dayStatuses) {
    if (s.eventDay === day) statusOf.set(s.exhibitorId, s);
  }
  const today = EXHIBIT_DAY_OF_EVENT_DAY[day];

  return exhibitors.map((exhibitor) => {
    const recs = byExhibitor.get(exhibitor.id) ?? [];
    const checkedInAt = earliest(recs.filter((r) => r.eventDay === day && r.kind === 'checkin'));
    const saturdayCheckedInAt = earliest(recs.filter((r) => r.eventDay === 1 && r.kind === 'checkin'));
    const exhibitsToday = exhibitor.exhibitDays.includes(today);
    const paid = exhibitor.status === 'paid';
    const notRequired =
      day === 2 && exhibitor.exhibitDays.includes('saturday') && saturdayCheckedInAt !== null;
    const status = statusOf.get(exhibitor.id);
    const absent = status?.status === 'absent' && checkedInAt === null;
    const late = status?.status === 'late' ? { expectedTime: status.expectedTime ?? null } : null;
    const lastAbsentSeenAt = latest(recs.filter((r) => r.eventDay === day && r.kind === 'absent_seen'));
    return {
      exhibitor,
      exhibitsToday,
      paid,
      checkedInAt,
      notRequired,
      saturdayCheckedInAt,
      absent,
      late,
      lastAbsentSeenAt,
      needsCheckin: paid && exhibitsToday && !notRequired && !absent,
    };
  });
}

export interface DaySummary {
  // 本日の受付 done / total
  done: number;
  total: number;
  // 未受付の一覧 (配置番号順)
  pending: ExhibitorDayState[];
  lateCount: number;
  absentSeenCount: number;
}

export function summarizeDay(states: ExhibitorDayState[]): DaySummary {
  const needed = states.filter((s) => s.needsCheckin);
  const pending = needed
    .filter((s) => s.checkedInAt === null)
    .sort((a, b) => compareExhibitorsByBooth(a.exhibitor, b.exhibitor));
  return {
    done: needed.length - pending.length,
    total: needed.length,
    pending,
    lateCount: pending.filter((s) => s.late).length,
    absentSeenCount: pending.filter((s) => s.lastAbsentSeenAt).length,
  };
}

// ─── 受付端末で受付ボタンを押したときの結果 ───────────────

export type KioskOutcome =
  | { type: 'ok' }
  // 同じ日に 2 回目
  | { type: 'already'; checkedInAt: string }
  // 日曜・土曜に受付済みの両日出展者
  | { type: 'not_required'; saturdayCheckedInAt: string }
  // スタッフ対応が必要
  | { type: 'blocked'; reason: BlockedReason };

export function decideKioskOutcome(state: ExhibitorDayState): KioskOutcome {
  const status = state.exhibitor.status;
  if (status !== 'paid') {
    if (status === 'waitingForPay') return { type: 'blocked', reason: 'unpaid' };
    if (status === 'cancelled') return { type: 'blocked', reason: 'cancelled' };
    return { type: 'blocked', reason: 'not_confirmed' };
  }
  if (!state.exhibitsToday) return { type: 'blocked', reason: 'wrong_day' };
  if (state.notRequired && state.saturdayCheckedInAt) {
    return { type: 'not_required', saturdayCheckedInAt: state.saturdayCheckedInAt };
  }
  if (state.checkedInAt) return { type: 'already', checkedInAt: state.checkedInAt };
  return { type: 'ok' };
}
